import photoOffIcon from "@tabler/icons/outline/photo-off.svg";
import React, { useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import GroupAvatar from "@/components/groups/group-avatar";
import StillImage from "@/components/still-image";
import { HStack, Icon, Stack, Text } from "@/components/ui";
import { useModalContext } from "@/contexts/modal-context";
import { type Attachment, Group } from "@/schemas";
import { isDefaultHeader } from "@/utils/accounts";
import { cn } from "@/utils/cn";

import GroupActionButton from "./group-action-button";
import GroupMemberCount from "./group-member-count";
import GroupOptionsButton from "./group-options-button";
import GroupPrivacy from "./group-privacy";
import GroupRelationship from "./group-relationship";
import GroupSearchButton from "./group-search-button";
import GroupSubscriptionButton from "./group-subscription-button";

const messages = defineMessages({
  header: { id: "group.header.alt", defaultMessage: "Group header" },
});

interface GroupHeaderProps {
  group?: Group | false | null;
  isPredictionGroup?: boolean;
}

const GroupHeader: React.FC<GroupHeaderProps> = ({
  group,
  isPredictionGroup = false,
}) => {
  const intl = useIntl();
  const { openModal } = useModalContext();

  const [isHeaderMissing, setIsHeaderMissing] = useState<boolean>(false);

  if (!group) {
    return (
      <div data-testid="group-header-missing">
        <div>
          <div className="relative h-32 w-full bg-gray-200 md:rounded-t-xl lg:h-48 dark:bg-gray-900/50" />
        </div>

        <div className="px-4 sm:px-6">
          <HStack alignItems="bottom" space={5} className="-mt-12">
            <div className="relative flex">
              <div className="size-24 rounded-full bg-gray-400 ring-4 ring-white dark:ring-gray-800" />
            </div>
          </HStack>
        </div>
      </div>
    );
  }

  const isDeleted = !!group.deleted_at;

  const onAvatarClick = () => {
    const avatar = {
      type: "image",
      url: group.avatar_static,
    } as Attachment;
    openModal("MEDIA", { media: [{ attachment: avatar }] });
  };

  const handleAvatarClick: React.MouseEventHandler = (e) => {
    if (e.button === 0 && !(e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      onAvatarClick();
    }
  };

  const onHeaderClick = () => {
    const header = {
      type: "image",
      url: group.header_static,
    } as Attachment;
    openModal("MEDIA", { media: [{ attachment: header }] });
  };

  const handleHeaderClick: React.MouseEventHandler = (e) => {
    if (e.button === 0 && !(e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      onHeaderClick();
    }
  };

  const renderHeader = () => {
    let header: React.ReactNode;

    if (group.header_static) {
      header = (
        <StillImage
          src={group.header_static}
          alt={intl.formatMessage(messages.header)}
          className="relative h-32 w-full bg-gray-200 object-center lg:h-48 dark:bg-gray-900/50"
          onError={() => setIsHeaderMissing(true)}
        />
      );

      if (!isDefaultHeader(group.header_static)) {
        header = (
          <a
            href={group.header_static}
            onClick={handleHeaderClick}
            target="_blank"
            className="relative w-full"
          >
            {header}
          </a>
        );
      }
    }

    return (
      <div
        data-testid="group-header-image"
        className="flex h-32 w-full items-center justify-center bg-gray-200 lg:h-48 dark:bg-gray-800/30"
      >
        {isHeaderMissing ? (
          <Icon
            src={photoOffIcon}
            className="size-6 text-gray-500 dark:text-gray-700"
          />
        ) : (
          header
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="relative">
        {renderHeader()}

        {!isPredictionGroup && (
          <div
            className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2"
            data-testid="group-avatar"
          >
            <a
              href={group.avatar_static}
              onClick={handleAvatarClick}
              target="_blank"
            >
              <GroupAvatar group={group} size={96} withRing />
            </a>
          </div>
        )}
      </div>

      <Stack
        alignItems="center"
        space={3}
        className={cn("mx-auto mt-12 p-4 sm:w-5/6 sm:px-0", {
          "mt-7": isPredictionGroup,
        })}
      >
        <Text
          size="xl"
          weight="bold"
          dangerouslySetInnerHTML={{ __html: group.display_name_html }}
          data-testid="group-name"
          align="center"
        />

        {!isDeleted && !isPredictionGroup && (
          <>
            <Stack data-testid="group-meta" space={1} alignItems="center">
              <HStack
                className="text-muted-foreground"
                space={3}
                wrap
                alignItems="center"
              >
                <GroupRelationship group={group} />
                <GroupPrivacy group={group} />
                <GroupMemberCount group={group} />
              </HStack>

              <Text
                theme="muted"
                align="center"
                dangerouslySetInnerHTML={{ __html: group.note_emojified }}
                className="[&_a]:text-primary-600 hover:[&_a]:underline dark:[&_a]:text-primary-400"
              />
            </Stack>

            <HStack alignItems="stretch" space={2} data-testid="group-actions">
              <GroupSearchButton group={group} />
              <GroupSubscriptionButton group={group} />
              <GroupOptionsButton group={group} />
              <GroupActionButton group={group} />
            </HStack>
          </>
        )}
      </Stack>
    </div>
  );
};

export default GroupHeader;
